import { MAX_PLUS_ONES } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Settings2 } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useOrganizerProfile } from '~/entities/channel/model/use-organizer'
import { PROFILE_LINK_ANCHOR } from '~/entities/profile-link/model/profile-link-anchor'
import { type EventPrivacy, offersMembersPrivacy } from '~/features/events/lib/event-privacy'
import { moreSettingsSummary } from '~/features/events/lib/more-settings'
import { AmenitySelector } from '~/features/shared/components/amenity-selector'
import * as m from '~/paraglide/messages'
import { Card, CardContent, CardHeader, CardTitle } from '~/shared/ui/card'
import { Disclosure } from '~/shared/ui/disclosure'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { SettingRow, SwitchRow } from './setting-row'

export const EVENT_FEATURES_ANCHOR = 'event-features'

// biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
type AnyForm = any

const AMENITIES_COLLAPSED_LIMIT = 12

export function EventFeaturesSection({
  form,
  collapsible = false,
}: {
  form: AnyForm
  collapsible?: boolean
}) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [openFromLink] = useState(
    () => typeof window !== 'undefined' && window.location.hash === `#${EVENT_FEATURES_ANCHOR}`,
  )

  useEffect(() => {
    if (openFromLink) sectionRef.current?.scrollIntoView({ block: 'start' })
  }, [openFromLink])

  if (!collapsible) {
    return (
      <Card ref={sectionRef} id={EVENT_FEATURES_ANCHOR} className="scroll-mt-6">
        <CardHeader>
          <CardTitle>{m.moreSettingsTitle()}</CardTitle>
        </CardHeader>
        <CardContent>
          <SettingsGroups form={form} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card ref={sectionRef} id={EVENT_FEATURES_ANCHOR} className="scroll-mt-6 px-4 py-2 sm:px-6">
      <form.Subscribe selector={(state: { values: never }) => moreSettingsSummary(state.values)}>
        {(summary: string) => (
          <Disclosure
            title={m.moreSettingsTitle()}
            hint={summary}
            icon={<Settings2 />}
            defaultOpen={openFromLink}
            contentClassName="pb-4"
          >
            <SettingsGroups form={form} />
          </Disclosure>
        )}
      </form.Subscribe>
    </Card>
  )
}

function SettingsGroups({ form }: { form: AnyForm }) {
  return (
    <div className="space-y-5">
      <SettingsGroup title={m.settingsGroupAccess()}>
        <VisibilityField form={form} />
      </SettingsGroup>
      <SettingsGroup title={m.liveRoomFeature()}>
        <LiveRoomModeField form={form} />
      </SettingsGroup>
      <SettingsGroup title={m.settingsGroupAudience()}>
        <div className="grid gap-x-8 sm:grid-cols-2">
          <MinAgeField form={form} />
          <StudentsOnlyField form={form} />
          <TargetAgeField form={form} />
          <AudienceNoteField form={form} />
        </div>
      </SettingsGroup>
      <SettingsGroup title={m.advancedTitle()}>
        <form.Field name="tags.amenityIds">
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <div className="py-2.5">
              <AmenitySelector
                selectedAmenityIds={field.state.value || []}
                collapsedLimit={AMENITIES_COLLAPSED_LIMIT}
                onAmenitiesChange={(amenityIds) => field.handleChange(amenityIds)}
              />
            </div>
          )}
        </form.Field>
      </SettingsGroup>
    </div>
  )
}

function SettingsGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1">
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">{title}</h4>
      <div className="divide-y">{children}</div>
    </section>
  )
}

const VISIBILITY_OPTIONS = [
  {
    value: 'public',
    label: () => m.eventVisibilityPublic(),
    description: () => m.eventVisibilityPublicDescription(),
  },
  {
    value: 'request',
    label: () => m.eventVisibilityRequest(),
    description: () => m.eventVisibilityRequestDescription(),
  },
  {
    value: 'private',
    label: () => m.visibilityPrivateShort(),
    description: () => m.eventPrivateFeatureDescription(),
  },
  {
    value: 'members',
    label: () => m.visibilityMembersShort(),
    description: () => m.eventVisibilityMembersDescription(),
  },
] as const

type VisibilityChoiceValue = (typeof VISIBILITY_OPTIONS)[number]['value']

function VisibilityField({ form }: { form: AnyForm }) {
  return (
    <form.Subscribe
      selector={(state: { values: { hostProfileId?: string | null } }) =>
        state.values.hostProfileId ?? null
      }
    >
      {(hostProfileId: string | null) => (
        <VisibilityChoice form={form} hostProfileId={hostProfileId} />
      )}
    </form.Subscribe>
  )
}

function VisibilityChoice({
  form,
  hostProfileId,
}: {
  form: AnyForm
  hostProfileId: string | null
}) {
  const { data: business } = useCurrentBusiness()
  const host = useOrganizerProfile(business?.id ?? null, hostProfileId ?? undefined)
  return (
    <form.Field name="privacy">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => {
        const privacy: EventPrivacy = field.state.value ?? 'public'
        const chosen: VisibilityChoiceValue = privacy === 'publicWithInvites' ? 'public' : privacy
        const options = VISIBILITY_OPTIONS.filter(
          (option) =>
            option.value !== 'members' || offersMembersPrivacy(host.data?.membersEnabled, privacy),
        )
        const selected = VISIBILITY_OPTIONS.find((option) => option.value === chosen)
        const choose = (value: VisibilityChoiceValue) => {
          if (value === chosen) return
          field.handleChange(value)
          if (value === 'members' && !hostProfileId && host.data) {
            form.setFieldValue('hostProfileId', host.data.id)
          }
        }
        return (
          <div className="space-y-2 py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <span className="font-medium text-sm">{m.eventVisibilityLabel()}</span>
              <SegmentedControl<VisibilityChoiceValue>
                size="sm"
                label={m.eventVisibilityLabel()}
                value={chosen}
                options={options.map((option) => ({ value: option.value, label: option.label() }))}
                onChange={choose}
              />
            </div>
            {selected ? (
              <p className="text-muted-foreground text-xs">{selected.description()}</p>
            ) : null}
            {host.data && !host.data.membersEnabled && privacy !== 'members' ? (
              <p className="text-muted-foreground text-xs">
                {m.membersOffShortHint()}{' '}
                <Link
                  to="/dashboard/organizer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-foreground underline underline-offset-4"
                >
                  {m.eventMembersOffLink()}
                </Link>
              </p>
            ) : null}
            {privacy === 'request' ? (
              <>
                <div className="grid gap-x-8 sm:grid-cols-2">
                  <RequestEmailsField form={form} />
                  <PlusOnesField form={form} hint={m.rowRequestCompanionsHint()} />
                </div>
                <p className="text-muted-foreground text-xs">
                  {m.rowRequestPhoneHint()}{' '}
                  <Link
                    to="/dashboard/settings"
                    hash={PROFILE_LINK_ANCHOR}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-foreground underline underline-offset-4"
                  >
                    {m.eventRequestPhoneLink()}
                  </Link>
                </p>
              </>
            ) : null}
            {privacy === 'private' || privacy === 'members' || privacy === 'publicWithInvites' ? (
              <>
                {privacy === 'members' ? (
                  <p className="text-muted-foreground text-xs">{m.eventMembersHint()}</p>
                ) : null}
                {privacy === 'publicWithInvites' ? (
                  <p className="text-muted-foreground text-xs">{m.eventPublicWithInvitesHint()}</p>
                ) : null}
                <div className="grid gap-x-8 sm:grid-cols-2">
                  <RsvpField form={form} />
                  <PlusOnesField form={form} hint={m.rowPlusOnesHint()} />
                </div>
              </>
            ) : null}
          </div>
        )
      }}
    </form.Field>
  )
}

function RequestEmailsField({ form }: { form: AnyForm }) {
  return (
    <form.Field name="requestEmails">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <SwitchRow
          id={field.name}
          label={m.requestEmailsFeature()}
          hint={m.rowRequestEmailsHint()}
          checked={field.state.value !== false}
          onCheckedChange={(checked) => field.handleChange(checked)}
        />
      )}
    </form.Field>
  )
}

const DEFAULT_PLUS_ONES = 1
const MIN_AGE_EVERYONE = 'everyone'
const TARGET_AGE_MIN = 14
const TARGET_AGE_MAX = 99

function toNumberOrUndefined(value: string): number | undefined {
  return value === '' ? undefined : Number(value)
}

function PlusOnesField({ form, hint }: { form: AnyForm; hint: string }) {
  return (
    <form.Field name="plusOnesMax">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => {
        const max = Number(field.state.value ?? 0)
        return (
          <SwitchRow
            id={field.name}
            label={m.plusOnesFeature()}
            hint={hint}
            checked={max > 0}
            onCheckedChange={(checked) => field.handleChange(checked ? DEFAULT_PLUS_ONES : 0)}
          >
            <div className="flex items-center gap-3">
              <Label htmlFor={`${field.name}-count`} className="font-normal text-sm">
                {m.plusOnesUpTo()}
              </Label>
              <Input
                id={`${field.name}-count`}
                type="number"
                min={1}
                max={MAX_PLUS_ONES}
                className="h-8 w-20"
                value={max}
                onChange={(e) => {
                  const next = Math.round(Number(e.target.value))
                  field.handleChange(Math.min(MAX_PLUS_ONES, Math.max(1, next || 1)))
                }}
              />
            </div>
          </SwitchRow>
        )
      }}
    </form.Field>
  )
}

function RsvpField({ form }: { form: AnyForm }) {
  return (
    <form.Field name="rsvpEnabled">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <SwitchRow
          id={field.name}
          label={m.rsvpFeature()}
          hint={m.rowRsvpHint()}
          checked={field.state.value === true}
          onCheckedChange={(checked) => field.handleChange(checked)}
        />
      )}
    </form.Field>
  )
}

function LiveRoomModeField({ form }: { form: AnyForm }) {
  return (
    <form.Field name="liveRoomMode">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <SwitchRow
          id={field.name}
          label={m.liveRoomFeature()}
          hint={m.liveRoomRowHint()}
          checked={field.state.value !== 'off'}
          onCheckedChange={(checked) => field.handleChange(checked ? 'open' : 'off')}
        />
      )}
    </form.Field>
  )
}

function MinAgeField({ form }: { form: AnyForm }) {
  return (
    <form.Field name="minAge">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <SettingRow
          id={field.name}
          label={m.minAgeFeature()}
          hint={m.rowMinAgeHint()}
          control={
            <Select
              value={field.state.value == null ? MIN_AGE_EVERYONE : String(field.state.value)}
              onValueChange={(value) =>
                field.handleChange(value === MIN_AGE_EVERYONE ? null : Number(value))
              }
            >
              <SelectTrigger id={field.name} size="sm" className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={MIN_AGE_EVERYONE}>{m.minAgeEveryone()}</SelectItem>
                <SelectItem value="16">{m.minAgeBadge({ age: '16' })}</SelectItem>
                <SelectItem value="18">{m.minAgeBadge({ age: '18' })}</SelectItem>
              </SelectContent>
            </Select>
          }
        />
      )}
    </form.Field>
  )
}

function StudentsOnlyField({ form }: { form: AnyForm }) {
  return (
    <form.Field name="studentsOnly">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <SwitchRow
          id={field.name}
          label={m.studentsOnlyFeature()}
          hint={m.rowStudentsOnlyHint()}
          checked={field.state.value === true}
          onCheckedChange={(checked) => field.handleChange(checked)}
        />
      )}
    </form.Field>
  )
}

function TargetAgeField({ form }: { form: AnyForm }) {
  return (
    <SettingRow
      label={m.targetAgeLabel()}
      hint={m.rowTargetAgeHint()}
      control={
        <div className="flex items-center gap-1.5">
          <form.Field name="targetAgeMin">
            {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
            {(field: any) => (
              <Input
                aria-label={m.targetAgeFrom()}
                type="number"
                min={TARGET_AGE_MIN}
                max={TARGET_AGE_MAX}
                className="h-8 w-16"
                placeholder={m.targetAgeFrom()}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(toNumberOrUndefined(e.target.value))}
              />
            )}
          </form.Field>
          <span className="text-muted-foreground">–</span>
          <form.Field name="targetAgeMax">
            {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
            {(field: any) => (
              <Input
                aria-label={m.targetAgeTo()}
                type="number"
                min={TARGET_AGE_MIN}
                max={TARGET_AGE_MAX}
                className="h-8 w-16"
                placeholder={m.targetAgeTo()}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(toNumberOrUndefined(e.target.value))}
              />
            )}
          </form.Field>
        </div>
      }
    />
  )
}

function AudienceNoteField({ form }: { form: AnyForm }) {
  return (
    <form.Field name="audienceNote">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <div className="space-y-1.5 py-2.5">
          <Label htmlFor={field.name}>{m.audienceNoteLabel()}</Label>
          <Input
            id={field.name}
            maxLength={200}
            className="h-8"
            value={field.state.value ?? ''}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
            placeholder={m.audienceNotePlaceholder()}
          />
        </div>
      )}
    </form.Field>
  )
}
